import { Check, Cloud, Container, FolderOpen, Laptop, MonitorPlay, Square } from "lucide-react";
import { create } from "zustand";
import { can } from "@ide/shared";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/controls";
import { Popover, PopoverContent, PopoverTrigger, Tip } from "@/components/ui/overlay";
import { desktop } from "@/desktop/bridge";
import { hasFeature } from "@/desktop/edition";
import { openLocalFolderInEditor } from "@/desktop/local-folder";
import { cn, timeAgo } from "@/lib/utils";
import { activeRun, runsAvailable, startLive, stopRun, useLocalRuns } from "@/runs/local-runs";
import { openRunDialog } from "@/runs/runs-ui";
import { useRole, useTree, useWs } from "./context";
import { activeWorkspace } from "./file-ops";
import { disableLocalCopy, enableLocalCopy, localCopyAvailable, useLocalCopy } from "./local-copy";

/**
 * Onde o projeto roda para VOCÊ (o código é sempre o mesmo para o time):
 *  - Nuvem: terminal no sandbox do Kompil;
 *  - Local: pasta no seu computador (cópia local) e o seu terminal — funciona sem internet;
 *  - Contêiner: Compose/Dockerfile/imagem no Kompil Engine, em localhost.
 * Substitui o antigo "Só na nuvem" e absorve o "Rodar na sua máquina".
 */
export type Environment = "cloud" | "local" | "container";

const KEY = (projectId: string) => `kompil.environment.${projectId}`;

function readPref(projectId: string): "cloud" | "local" | null {
  try {
    const v = localStorage.getItem(KEY(projectId));
    return v === "cloud" || v === "local" ? v : null;
  } catch {
    return null;
  }
}

/** Preferência de terminal por projeto (Nuvem ou Local), lembrada neste computador. */
export const useEnvPreference = create<{
  prefs: Record<string, "cloud" | "local" | null>;
  set(projectId: string, v: "cloud" | "local"): void;
}>((set, get) => ({
  prefs: {},
  set: (projectId, v) => {
    set({ prefs: { ...get().prefs, [projectId]: v } });
    try {
      localStorage.setItem(KEY(projectId), v);
    } catch {
      // sem armazenamento: vale nesta sessão
    }
  },
}));

export function envPreference(projectId: string): "cloud" | "local" | null {
  const mem = useEnvPreference.getState().prefs[projectId];
  return mem !== undefined ? mem : readPref(projectId);
}

/** A pessoa tem a nuvem (plano Business). Versões antigas da API não informam: liberada. */
export function hasCloud(): boolean {
  return activeWorkspace.current?.me.user.cloud !== false;
}

/** Terminal novo: o do ambiente escolhido (Local só com a cópia local ligada; sem Business, Local). */
export function preferredTerminal(): "local" | "sandbox" {
  const projectId = activeWorkspace.current?.detail.project.id;
  const localOn = localCopyAvailable && Boolean(useLocalCopy.getState().info?.enabled);
  if (!hasCloud() && localCopyAvailable) return "local";
  if (!localOn) return "sandbox";
  return projectId && envPreference(projectId) === "cloud" ? "sandbox" : "local";
}

/** Ambiente atual do projeto para esta pessoa. */
export function useEnvironment(): Environment {
  const { detail, me } = useWs();
  const container = useLocalRuns((s) => Boolean(activeRun(s.runs, detail.project.id, "container")));
  const localOn = useLocalCopy((s) => Boolean(s.info?.enabled) && s.state !== "error");
  const pref = useEnvPreference((s) => s.prefs[detail.project.id]) ?? readPref(detail.project.id);
  if (container) return "container";
  if (localOn && (pref !== "cloud" || me.user.cloud === false)) return "local";
  return me.user.cloud === false && localCopyAvailable ? "local" : "cloud";
}

export const ENV_META: Record<Environment, { label: string; icon: typeof Cloud; hint: string }> = {
  cloud: {
    label: "Nuvem",
    icon: Cloud,
    hint: "Contêiner só seu com Node, Python, Java, Go, Rust, PHP e Ruby, com os arquivos do projeto. O que você cria ou muda no terminal volta para o projeto e fica salvo.",
  },
  local: {
    label: "Local",
    icon: Laptop,
    hint: "Pasta no seu computador e o seu terminal. Continua funcionando sem internet.",
  },
  container: {
    label: "Contêiner",
    icon: Container,
    hint: "Compose, Dockerfile ou imagem, em localhost.",
  },
};

/** Seletor de ambiente na barra do projeto (Nuvem / Local / Contêiner). */
export function EnvironmentControl() {
  const { session, detail, me } = useWs();
  const role = useRole();
  const tree = useTree();
  const env = useEnvironment();
  const { info, state, savedAt, error } = useLocalCopy();
  const live = useLocalRuns((s) => activeRun(s.runs, detail.project.id, "live"));
  const container = useLocalRuns((s) => activeRun(s.runs, detail.project.id, "container"));
  const project = { id: detail.project.id, name: detail.project.name };
  const terminal = hasFeature("terminal") && can(role, "terminal");
  const site = [...tree.paths.values()].some((p) => p === "index.html" || p.endsWith("/index.html"));
  const Icon = ENV_META[env].icon;

  const choose = async (next: Environment) => {
    if (next === "container") return openRunDialog(project);
    if (next === "local") {
      if (!localCopyAvailable) return;
      if (!info?.enabled && !(await enableLocalCopy(session, project, false))) return;
    }
    useEnvPreference.getState().set(project.id, next);
  };

  const option = (id: Environment, disabled: boolean, extra?: React.ReactNode) => {
    const meta = ENV_META[id];
    const OptIcon = meta.icon;
    const on = env === id;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={on}
        disabled={disabled}
        onClick={() => void choose(id)}
        className={cn(
          "flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          on
            ? "border-accent/60 bg-accent-soft"
            : "border-border hover:border-border-strong hover:bg-bg-muted/60",
        )}
      >
        <OptIcon className={cn("mt-0.5 h-4 w-4 shrink-0", on ? "text-accent" : "text-fg-muted")} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-[13px] font-medium text-fg">
            {meta.label}
            {on ? <Check className="h-3.5 w-3.5 text-accent" /> : null}
          </span>
          <span className="mt-0.5 block text-[12px] leading-snug text-fg-muted">{meta.hint}</span>
          {extra}
        </span>
      </button>
    );
  };

  return (
    <Popover>
      <Tip label={`Ambiente: ${ENV_META[env].label} — onde o projeto roda para você`}>
        <PopoverTrigger asChild>
          <button
            className="flex h-8 items-center gap-1.5 rounded-md border border-border-strong px-2.5 text-[12.5px] font-medium text-fg transition-colors hover:bg-bg-muted data-[state=open]:bg-bg-emphasis"
            aria-label={`Ambiente: ${ENV_META[env].label}`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">{ENV_META[env].label}</span>
            {state === "starting" || container?.status === "starting" ? (
              <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-fg" />
            ) : null}
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent align="end" className="w-[360px] space-y-2 p-3">
        <p className="px-0.5 text-[12px] font-medium uppercase tracking-[0.08em] text-fg-muted">
          Onde o projeto roda para você
        </p>
        <div role="radiogroup" aria-label="Ambiente" className="space-y-1.5">
          {option(
            "cloud",
            !terminal || me.user.cloud === false,
            me.user.cloud === false ? (
              <span className="rounded border border-border px-1 text-[10px] uppercase tracking-wide text-fg-muted">
                Business
              </span>
            ) : undefined,
          )}
          {option(
            "local",
            !localCopyAvailable,
            !localCopyAvailable ? (
              <span className="mt-1 block text-[11.5px] text-fg-subtle">Disponível no Kompil Desktop.</span>
            ) : null,
          )}
          {option(
            "container",
            !runsAvailable,
            container ? (
              <span className="mt-1.5 flex items-center gap-2 text-[11.5px] text-fg-muted">
                rodando{container.url ? ` em ${container.url.replace(/^http:\/\//, "")}` : ""}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    void stopRun(container.id);
                  }}
                  className="inline-flex items-center gap-1 rounded px-1 text-fg hover:bg-bg-emphasis"
                >
                  <Square className="h-2.5 w-2.5" /> parar
                </span>
              </span>
            ) : null,
          )}
        </div>
        {localCopyAvailable && info ? (
          <div className="rounded-lg border border-border bg-bg p-2.5 text-[12px]">
            <label className="flex items-center justify-between gap-3">
              <span className="font-medium text-fg">Salvar também na máquina</span>
              <Switch
                checked={info.enabled}
                onCheckedChange={(v) =>
                  void (v ? enableLocalCopy(session, project, false) : disableLocalCopy(project))
                }
                aria-label="Salvar na máquina local"
              />
            </label>
            <p className="mt-1.5 break-all font-mono text-[11.5px] text-fg-muted">
              {info.path ?? info.suggested}
            </p>
            {info.enabled && savedAt ? (
              <p className="mt-1 text-fg-subtle">Último salvamento {timeAgo(savedAt)}</p>
            ) : null}
            {state === "error" ? <p className="mt-1 text-[var(--danger)]">{error}</p> : null}
            <div className="mt-2 flex flex-col items-start gap-1.5">
              <Button size="sm" variant="secondary" onClick={() => void openLocalFolderInEditor()}>
                <FolderOpen className="h-3.5 w-3.5" /> Abrir pasta existente…
              </Button>
              <Button size="sm" variant="ghost" onClick={() => void enableLocalCopy(session, project, true)}>
                Escolher pasta para salvar…
              </Button>
              {info.path ? (
                <Button size="sm" variant="ghost" onClick={() => desktop?.local.reveal(project.id)}>
                  Mostrar no computador
                </Button>
              ) : null}
            </div>
            <p className="mt-1 text-[11.5px] text-fg-subtle">
              A pasta (com o código e as subpastas) entra neste projeto e fica sincronizada; com .git, o
              repositório liga sozinho.
            </p>
            <p className="mt-2 text-[11.5px] leading-relaxed text-fg-subtle">
              Ficam só no computador: .git, node_modules, .venv, builds, binários e o seu .env.
            </p>
          </div>
        ) : null}
        {site && runsAvailable ? (
          <Button
            variant="secondary"
            className="w-full justify-center"
            onClick={() => void startLive(project)}
          >
            <MonitorPlay className="h-3.5 w-3.5" />{" "}
            {live ? "Abrir a pré-visualização" : "Pré-visualizar o site"}
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
